/**
=========================================================
* Soft UI Dashboard PRO React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAvatar from "components/SuiAvatar";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

// Soft UI Dashboard PRO React example components
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import DocumentIcon from "examples/Icons/Document";
import SettingsIcon from "examples/Icons/Settings";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
// Images
import burceMars from "assets/images/bruce-mars.jpg";
import curved0 from "assets/images/curved-images/curved0.jpg";
import Rating from "@mui/material/Rating";
import { useNavigate } from "react-router-dom";

// Images
import ImgCrop from "antd-img-crop";
import { Upload } from "antd";
import { getBase64, dataURLtoFile } from "utils/imageHelper";

import api from "services/api";
import { nanoid } from "nanoid";
import { useForm, Controller } from "react-hook-form";
import { addProtocol, formatAddress } from "utils/helper";

const Header = ({ store, hideButton = false, statusBar = null, editable = true, updateStore }) => {
  const [editVisible, setEditVisible] = useState(false);
  const [avatarImageUrl, setAvatarImageUrl] = useState("");
  const [bgImageUrl, setBgImageUrl] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    setValue,
    getValues,
    reset,

    control,
  } = useForm({});

  let instagram_link = "#";
  if (store.metadata && store.metadata.instagram_link) {
    instagram_link = store.metadata.instagram_link;
  }
  const navigate = useNavigate();

  useEffect(() => {
    reset({
      name: store.name,
      detail: store.detail,
    });
    setAvatarImageUrl(store?.metadata?.avatar);
    setBgImageUrl(store?.metadata?.background_image);
  }, [store]);

  const handleShowEdit = (e) => {
    setEditVisible(true);
  };

  const handleSave = async (data) => {
    let avatarUrl = avatarImageUrl;
    let bgUrl = bgImageUrl;

    if (avatarImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(avatarImageUrl, "company_avatar");

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      avatarUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    if (bgImageUrl?.startsWith("data")) {
      const file = dataURLtoFile(bgImageUrl, nanoid());

      const uploadedImg = await api.uploads.create([file]).then(({ data }) => {
        const uploaded = data.uploads.map(({ url }) => url);
        return uploaded;
      });
      bgUrl = uploadedImg && uploadedImg.length > 0 ? uploadedImg[0] : "";
    }

    try {
      const payload = {
        name: data["name"],
        detail: data["detail"],
        metadata: {
          avatar: avatarUrl,
          background_image: bgUrl,
        },
      };
      await updateStore(payload);
      toast.success("Update Company information successfully!");
      setEditVisible(false);
    } catch (err) {
      toast.error("Update Company information failed!");
    }
  };

  return (
    <SuiBox position="relative">
      <div>
        <SuiBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight="18.75rem"
          borderRadius="xl"
          sx={{
            backgroundImage: bgImageUrl
              ? `url(${bgImageUrl})`
              : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                  `${linearGradient(
                    rgba(gradients.secondary.main, 0.2),
                    rgba(gradients.secondary.state, 0.4)
                  )}`,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
      </div>
      <Card
        className="overflow-visible"
        sx={{
          backdropFilter: `saturate(200%) blur(30px)`,
          backgroundColor: ({ functions: { rgba }, palette: { white } }) => rgba(white.main, 0.8),
          boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="flex flex-wrap justify-start min-h-[50px] ml-8">
            <div className="w-24">
              <div className="absolute -top-10 left-8">
                <SuiAvatar
                  src={avatarImageUrl}
                  alt="profile-image"
                  variant="circle"
                  size="xxl"
                  shadow="sm"
                />
              </div>
            </div>
            <div className="flex justify-between grow  xsmall:pt-12 xsmall:pb-4 small:pt-0">
              {statusBar ? (
                statusBar
              ) : (
                <div className="flex flex-wrap justify-start w-full ml-1">
                  <div className="flex flex-wrap items-center">
                    <Tooltip
                      title={
                        <SuiTypography
                          sx={{ mr: 2, ml: 1 }}
                          variant="text"
                          fontWeight="regular"
                          color="secondary"
                        >
                          {store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0} of 5 stars
                        </SuiTypography>
                      }
                    >
                      <SuiBox>
                        <Rating
                          name="half-rating-read"
                          value={store?.rating >= 0 ? (store.rating / 20).toFixed(1) : 0}
                          precision={0.1}
                          readOnly
                        />
                      </SuiBox>
                    </Tooltip>

                    <div className="flex gap-2">
                      <a
                        target={instagram_link == "#" ? "_self" : "_blank"}
                        href={instagram_link == "#" ? "#" : addProtocol(instagram_link)}
                        className="text-gray-800 mr-2"
                      >
                        <InstagramIcon />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-2 ml-4">
            <div className="grid grid-cols-3">
              <div className="col-span-2 md:col-span-2 sm:col-span-3">
                <SuiTypography variant="h4" fontWeight="bold" color="dark">
                  {store?.name}
                </SuiTypography>
                <div>
                  <SuiTypography variant="h6" fontWeight="regular" color="dark">
                    {store.detail ? (
                      store.detail
                    ) : (
                      <SuiTypography
                        variant="h6"
                        color="text"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        Brief one sentence description of your capabilities/services
                      </SuiTypography>
                    )}
                  </SuiTypography>
                </div>
                <SuiTypography variant="button" fontWeight="regular" color="text">
                  {store.metadata && store.metadata.address
                    ? formatAddress(store.metadata.address)
                    : ""}
                </SuiTypography>
              </div>
              {hideButton ? (
                typeof hideButton == "function" ? (
                  hideButton()
                ) : null
              ) : (
                <div className="col-span-1 medium:col-span-1 small:col-span-3 xsmall:col-span-3 ">
                  <div className="grid grid-cols-2">
                    <div className="my-1 md:col-span-1 sm:col-span-2 xs:col-span-2">
                      <SuiButton
                        size="small"
                        className="small:px-4 medium:px-4 whitespace-nowrap"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/edit-profile");
                        }}
                      >
                        <span className="flex items-center justify-center w-max-24">
                          <div className="mr-1">
                            <DocumentIcon />
                          </div>
                          <SuiTypography
                            variant="text"
                            textTransform="uppercase"
                            className="xsmall:hidden small:inline"
                          >
                            Edit Profile
                          </SuiTypography>
                        </span>
                      </SuiButton>
                    </div>
                    <div className="my-1 md:col-span-1 sm:col-span-2 xs:col-span-2">
                      <SuiButton
                        variant="contained"
                        size="small"
                        className="small:px-2 medium:px-4 whitespace-nowrap"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/produce/all-projects/create");
                        }}
                      >
                        <div className="flex items-center justify-center w-max-24 ">
                          <div>
                            <span className="mr-1 ">
                              <DesignServicesIcon className="w-4 h-[100%]" />
                            </span>
                          </div>
                          <SuiTypography
                            variant="text"
                            textTransform="uppercase"
                            className="xsmall:hidden small:inline"
                          >
                            Create Project
                          </SuiTypography>
                        </div>
                      </SuiButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </Card>
    </SuiBox>
  );
};

export default Header;
